/**
 * EmailRulesPanel · v2.7.45+E12 (Feb-2026)
 * ═════════════════════════════════════════════════════════════════════
 * Right-side drawer for managing the email-rule engine. Users can:
 *   • View all their rules (own + system-shared) sorted by priority.
 *   • Toggle enabled/disabled state inline.
 *   • Create new rules via a structured editor (criteria list + actions
 *     list — no JSON exposed to the user).
 *   • Delete rules.
 *   • Manually fire a rule against its target mailbox.
 *   • Run ALL enabled rules in one click.
 *
 * The drawer is intentionally narrow on the editor side so the live
 * mailbox stays visible behind it — supervisors can drop a rule in,
 * fire it, and watch matched messages disappear / get flagged in real
 * time on the message list.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  X, Plus, Trash2, Play, Filter, Loader2, Zap, Flag, Archive,
  AlertOctagon, Inbox, ChevronRight, Sparkles,
} from "lucide-react";

const CRITERIA_TYPES = [
  { value: "from_contains",    label: "From contains" },
  { value: "to_contains",      label: "To contains" },
  { value: "subject_contains", label: "Subject contains" },
  { value: "body_contains",    label: "Body / snippet contains" },
  { value: "has_attachment",   label: "Has attachment (size > 50 KB)" },
  { value: "header_match",     label: "Header matches" },
];

const ACTION_TYPES = [
  { value: "mark_read",      label: "Mark as read",            Icon: Inbox,        accent: "text-slate-600" },
  { value: "flag",           label: "Flag (\\Flagged)",        Icon: Flag,         accent: "text-amber-600" },
  { value: "important",      label: "Mark Important (escalate)", Icon: Zap,        accent: "text-red-600" },
  { value: "move_to_folder", label: "Move to folder…",         Icon: ChevronRight, accent: "text-sky-600" },
  { value: "mark_spam",      label: "Move to Spam",            Icon: AlertOctagon, accent: "text-orange-600" },
  { value: "delete",         label: "Move to Trash",           Icon: Trash2,       accent: "text-rose-600" },
];

export default function EmailRulesPanel({ accounts = [], onClose }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState({});
  const [editing, setEditing] = useState(null);    // current draft (new or edit)
  const [showEditor, setShowEditor] = useState(false);
  const [runningAll, setRunningAll] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/email/rules");
      setRules(r.data?.items || []);
    } catch (e) {
      toast.error("Failed to load rules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const startNew = () => {
    setEditing({
      name: "",
      account_id: "*",
      folder: "INBOX",
      enabled: true,
      priority: 100,
      criteria: [{ type: "from_contains", value: "", op: "and", case_sensitive: false }],
      actions:  [{ type: "flag", params: {} }],
    });
    setShowEditor(true);
  };

  const startEdit = (rule) => { setEditing({ ...rule }); setShowEditor(true); };

  const saveDraft = async () => {
    if (!editing.name?.trim()) { toast.error("Rule needs a name"); return; }
    if (!editing.criteria?.length) { toast.error("Add at least one criterion"); return; }
    if (!editing.actions?.length) { toast.error("Add at least one action"); return; }
    try {
      if (editing.id) {
        await adminApi.patch(`/admin/email/rules/${editing.id}`, editing);
        toast.success("Rule updated");
      } else {
        await adminApi.post("/admin/email/rules", editing);
        toast.success("Rule created");
      }
      setShowEditor(false);
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const toggleEnabled = async (rule) => {
    try {
      await adminApi.patch(`/admin/email/rules/${rule.id}`, { enabled: !rule.enabled });
      reload();
    } catch (e) {
      toast.error(`Toggle failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? This can't be undone.`)) return;
    try {
      await adminApi.delete(`/admin/email/rules/${rule.id}`);
      toast.success("Rule deleted");
      reload();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const runRule = async (rule) => {
    setRunning((r) => ({ ...r, [rule.id]: true }));
    try {
      const r = await adminApi.post(`/admin/email/rules/${rule.id}/run`);
      const summaries = r.data?.accounts || [];
      const totalMatched = summaries.reduce((s, a) => s + (a.matched || 0), 0);
      toast.success(`Rule fired · ${totalMatched} message${totalMatched === 1 ? "" : "s"} matched across ${summaries.length} mailbox${summaries.length === 1 ? "" : "es"}`);
      window.dispatchEvent(new CustomEvent("rtg-email-rules-fired"));
      reload();
    } catch (e) {
      toast.error(`Run failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning((r) => ({ ...r, [rule.id]: false }));
    }
  };

  const runAll = async () => {
    setRunningAll(true);
    try {
      const r = await adminApi.post("/admin/email/rules/run-all");
      const results = r.data?.results || [];
      const total = results.reduce((s, x) => s + (x.matched || 0), 0);
      toast.success(`All enabled rules fired · ${total} match${total === 1 ? "" : "es"} across ${results.length} rule×mailbox run${results.length === 1 ? "" : "s"}`);
      reload();
    } catch (e) {
      toast.error(`Run-all failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunningAll(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end"
      onClick={onClose}
      data-testid="email-rules-panel"
    >
      <div
        className="bg-white dark:bg-zinc-950 border-l border-slate-200 dark:border-zinc-800 w-full max-w-3xl h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-zinc-950 border-b border-slate-200 dark:border-zinc-800 px-5 py-3 flex items-center justify-between gap-2 z-10">
          <div className="flex items-center gap-2 min-w-0">
            <Filter className="h-5 w-5 text-sky-600 dark:text-sky-400 flex-shrink-0"/>
            <div className="font-bold text-lg truncate">Email Rules</div>
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-400 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 flex-shrink-0">
              {rules.length}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button size="sm" variant="outline" onClick={runAll} disabled={runningAll || rules.filter((r) => r.enabled).length === 0}
              className="gap-1.5 h-8 text-xs" data-testid="rules-run-all">
              {runningAll ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Play className="h-3.5 w-3.5"/>}
              Run all enabled
            </Button>
            <Button size="sm" onClick={startNew} className="bg-sky-600 hover:bg-sky-500 text-white gap-1.5 h-8 text-xs" data-testid="rules-new-btn">
              <Plus className="h-3.5 w-3.5"/>New rule
            </Button>
            <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900 dark:hover:text-zinc-100 p-1" aria-label="Close" data-testid="rules-close">
              <X className="h-5 w-5"/>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div className="text-xs text-slate-600 dark:text-zinc-400 bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-900/40 rounded p-3 flex gap-2 items-start">
            <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"/>
            <div className="leading-snug">
              Enabled rules also fire automatically every 5 minutes against their target mailbox.
              Each message is actioned at most once per rule — duplicate fires are deduplicated server-side.
            </div>
          </div>

          {loading && <div className="text-slate-500 text-sm py-4"><Loader2 className="h-4 w-4 inline-block animate-spin mr-2"/>Loading rules…</div>}

          {!loading && rules.length === 0 && (
            <div className="text-center py-10 border border-dashed border-slate-300 dark:border-zinc-700 rounded-lg">
              <Filter className="h-8 w-8 text-slate-400 dark:text-zinc-600 mx-auto mb-2"/>
              <div className="font-bold text-slate-700 dark:text-zinc-300">No rules yet</div>
              <div className="text-sm text-slate-500 dark:text-zinc-500 mt-1">Create a rule to auto-flag, archive, or escalate inbound mail.</div>
              <Button size="sm" onClick={startNew} className="bg-sky-600 hover:bg-sky-500 text-white mt-3 gap-1.5">
                <Plus className="h-3.5 w-3.5"/>Create your first rule
              </Button>
            </div>
          )}

          {!loading && rules.map((rule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              accounts={accounts}
              running={!!running[rule.id]}
              onToggle={() => toggleEnabled(rule)}
              onRun={() => runRule(rule)}
              onEdit={() => startEdit(rule)}
              onDelete={() => deleteRule(rule)}
            />
          ))}
        </div>

        {showEditor && editing && (
          <RuleEditor
            draft={editing}
            accounts={accounts}
            onChange={setEditing}
            onCancel={() => { setShowEditor(false); setEditing(null); }}
            onSave={saveDraft}
          />
        )}
      </div>
    </div>
  );
}

function RuleCard({ rule, accounts, running, onToggle, onRun, onEdit, onDelete }) {
  const acctLabel = useMemo(() => {
    if (rule.account_id === "*") return "All my mailboxes";
    const a = accounts.find((x) => x.id === rule.account_id);
    return a?.email || "Unknown account";
  }, [rule.account_id, accounts]);
  return (
    <div className="border border-slate-200 dark:border-zinc-800 rounded-lg p-3 bg-white dark:bg-zinc-900/40" data-testid={`rule-card-${rule.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm flex items-center gap-2">
            <span className="truncate">{rule.name}</span>
            <span className="text-[9px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-zinc-800">
              priority {rule.priority}
            </span>
            {rule.total_hits > 0 && (
              <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30">
                {rule.total_hits} hit{rule.total_hits === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-zinc-500 mt-0.5">
            {acctLabel} · {rule.folder} · {rule.criteria?.length || 0} criteria · {rule.actions?.length || 0} action{(rule.actions?.length || 0) === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Switch checked={!!rule.enabled} onCheckedChange={onToggle} data-testid={`rule-toggle-${rule.id}`}/>
          <Button size="sm" variant="outline" onClick={onRun} disabled={running} className="h-7 text-[11px] gap-1" data-testid={`rule-run-${rule.id}`}>
            {running ? <Loader2 className="h-3 w-3 animate-spin"/> : <Play className="h-3 w-3"/>}Run
          </Button>
          <Button size="sm" variant="ghost" onClick={onEdit} className="h-7 px-2 text-[11px]" data-testid={`rule-edit-${rule.id}`}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20" data-testid={`rule-delete-${rule.id}`}>
            <Trash2 className="h-3.5 w-3.5"/>
          </Button>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {(rule.criteria || []).map((c, i) => (
          <span key={`crit-${rule.id}-${i}-${c.type}`} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 font-mono">
            {CRITERIA_TYPES.find((t) => t.value === c.type)?.label || c.type}: <b>{c.value || "—"}</b>
          </span>
        ))}
        {(rule.actions || []).map((a, i) => {
          const meta = ACTION_TYPES.find((t) => t.value === a.type);
          const Icon = meta?.Icon || ChevronRight;
          return (
            <span key={`act-${rule.id}-${i}-${a.type}`} className={`text-[10px] px-1.5 py-0.5 rounded bg-sky-50 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300 flex items-center gap-1`}>
              <Icon className={`h-2.5 w-2.5 ${meta?.accent || ""}`}/>
              {meta?.label || a.type}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function RuleEditor({ draft, accounts, onChange, onCancel, onSave }) {
  const updateCrit = (idx, patch) => {
    const next = [...draft.criteria];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...draft, criteria: next });
  };
  const addCrit = () => onChange({ ...draft, criteria: [...draft.criteria, { type: "from_contains", value: "", op: "and", case_sensitive: false }] });
  const rmCrit  = (i) => onChange({ ...draft, criteria: draft.criteria.filter((_, x) => x !== i) });
  const updateAction = (idx, patch) => {
    const next = [...draft.actions];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...draft, actions: next });
  };
  const addAction = () => onChange({ ...draft, actions: [...draft.actions, { type: "flag", params: {} }] });
  const rmAction  = (i) => onChange({ ...draft, actions: draft.actions.filter((_, x) => x !== i) });
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onCancel} data-testid="rule-editor-modal">
      <div className="bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white dark:bg-zinc-950 border-b border-slate-200 dark:border-zinc-800 px-5 py-3 flex items-center justify-between">
          <div className="font-bold">{draft.id ? "Edit rule" : "New rule"}</div>
          <button type="button" onClick={onCancel} className="text-slate-500 hover:text-slate-900 dark:hover:text-zinc-100" data-testid="rule-editor-close">
            <X className="h-5 w-5"/>
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">Rule name</label>
            <Input value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} placeholder="e.g. Auto-flag Mercury Bank" data-testid="rule-name-input" className="mt-1"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">Target mailbox</label>
              <Select value={draft.account_id} onValueChange={(v) => onChange({ ...draft, account_id: v })}>
                <SelectTrigger data-testid="rule-account-select" className="mt-1"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="*">All my mailboxes</SelectItem>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">Folder</label>
              <Input value={draft.folder} onChange={(e) => onChange({ ...draft, folder: e.target.value })} placeholder="INBOX" data-testid="rule-folder-input" className="mt-1"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Switch checked={!!draft.enabled} onCheckedChange={(v) => onChange({ ...draft, enabled: v })} data-testid="rule-enabled-switch"/>
              <span className="text-sm font-bold">{draft.enabled ? "Enabled" : "Disabled"}</span>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">Priority (higher fires first)</label>
              <Input type="number" value={draft.priority} onChange={(e) => onChange({ ...draft, priority: Number(e.target.value) || 100 })} data-testid="rule-priority-input" className="mt-1"/>
            </div>
          </div>

          {/* Criteria */}
          <div className="border-t border-slate-200 dark:border-zinc-800 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold">Criteria</div>
              <Button size="sm" variant="outline" onClick={addCrit} className="gap-1 h-7 text-xs" data-testid="rule-add-criterion">
                <Plus className="h-3 w-3"/>Add
              </Button>
            </div>
            {draft.criteria.map((c, i) => (
              <div key={`edit-crit-${i}`} className="flex items-center gap-2 mb-2" data-testid={`rule-criterion-${i}`}>
                {i > 0 && (
                  <Select value={c.op || "and"} onValueChange={(v) => updateCrit(i, { op: v })}>
                    <SelectTrigger className="w-20 h-8 text-xs"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="and">AND</SelectItem>
                      <SelectItem value="or">OR</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <Select value={c.type} onValueChange={(v) => updateCrit(i, { type: v })}>
                  <SelectTrigger className="w-48 h-8 text-xs"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {CRITERIA_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {c.type === "header_match" && (
                  <Input value={c.header_name || ""} onChange={(e) => updateCrit(i, { header_name: e.target.value })} placeholder="Header name" className="w-32 h-8 text-xs"/>
                )}
                {c.type !== "has_attachment" && (
                  <Input value={c.value || ""} onChange={(e) => updateCrit(i, { value: e.target.value })} placeholder="value" className="flex-1 h-8 text-xs"/>
                )}
                <Button size="sm" variant="ghost" onClick={() => rmCrit(i)} className="h-7 w-7 p-0 text-rose-600"><Trash2 className="h-3.5 w-3.5"/></Button>
              </div>
            ))}
            {draft.criteria.length === 0 && (
              <div className="text-xs text-slate-500 italic">No criteria — add at least one to match messages.</div>
            )}
          </div>

          {/* Actions */}
          <div className="border-t border-slate-200 dark:border-zinc-800 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold">Actions (run in order)</div>
              <Button size="sm" variant="outline" onClick={addAction} className="gap-1 h-7 text-xs" data-testid="rule-add-action">
                <Plus className="h-3 w-3"/>Add
              </Button>
            </div>
            {draft.actions.map((a, i) => (
              <div key={`edit-act-${i}`} className="flex items-center gap-2 mb-2" data-testid={`rule-action-${i}`}>
                <Select value={a.type} onValueChange={(v) => updateAction(i, { type: v, params: {} })}>
                  <SelectTrigger className="w-56 h-8 text-xs"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {ACTION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {a.type === "move_to_folder" && (
                  <Input
                    value={a.params?.dest_folder || ""}
                    onChange={(e) => updateAction(i, { params: { ...(a.params || {}), dest_folder: e.target.value } })}
                    placeholder="Destination folder name (e.g. Archive)" className="flex-1 h-8 text-xs"
                  />
                )}
                <Button size="sm" variant="ghost" onClick={() => rmAction(i)} className="h-7 w-7 p-0 text-rose-600 ml-auto"><Trash2 className="h-3.5 w-3.5"/></Button>
              </div>
            ))}
            {draft.actions.length === 0 && (
              <div className="text-xs text-slate-500 italic">No actions — matched messages won&apos;t be modified.</div>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white dark:bg-zinc-950 border-t border-slate-200 dark:border-zinc-800 px-5 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} className="h-9" data-testid="rule-cancel">Cancel</Button>
          <Button onClick={onSave} className="bg-sky-600 hover:bg-sky-500 text-white h-9 gap-1.5" data-testid="rule-save">
            <Filter className="h-3.5 w-3.5"/>Save rule
          </Button>
        </div>
      </div>
    </div>
  );
}
